import { z } from "zod";
import type { ValidatableClass } from "../../schemas/validatableClass.js";
import { VALIDATE_METADATA_KEY } from "../decorators/constants.js";
import type { ValidateOptions } from "../decorators/validate.decorator.js";
import { augmentSchemasWithOpenApiMeta } from "./augment-schemas-with-open-api-meta.js";

/**
 * Normalized shape of a single @validate()-decorated field with its field name included.
 * Mirrors ValidateOptions but adds `key` so callers don't need to carry the name separately.
 */
export type FieldDescriptor = ValidateOptions & { key: string };

const ID_FIELD_NAME = "_id";

/** When hasDefault is true, allow omitting the field on create while keeping it required on responses. */
function optionalIfHasDefault(validator: z.ZodType, hasDefault: boolean | undefined): z.ZodType {
    if (!hasDefault) return validator;
    // If the field is not already optional, make it optional
    return validator.safeParse(undefined).success ? validator : validator.optional();
}

// ensure the _id field is tread as responseOnly even when the responseOnly flag is not set
function isResponseOnlyField(descriptor: FieldDescriptor): boolean {
    return descriptor.responseOnly === true || descriptor.key === ID_FIELD_NAME;
}

/**
 * Utility function that reads @validate() decorator metadata from a class and returns a flat list of field descriptors.
 * Reflect.getMetadata call walks the whole constructor prototype chain, so parent @validate decorators will be included in the child's dtoValidators.
 */
export function getClassFieldDescriptors(cls: unknown): FieldDescriptor[] {
    const metadata: Record<string, ValidateOptions> = Reflect.getMetadata(VALIDATE_METADATA_KEY, cls) ?? {};
    return Object.entries(metadata).map(([key, options]) => ({ key, ...options }));
}

/**
 * Gets all variants of the validation schema for a given class based on the decorators used
 * Note that the class constructor must satisfy {@link ValidatableClass}
 */
export function getZodSchemaFromClass(cls: ValidatableClass) {
    const fieldDescriptors = getClassFieldDescriptors(cls);

    const baseSchema = {};
    const baseLaxSchema = {};
    const createSchema = {};
    const createLaxSchema = {};
    const updateSchema = {};

    for (const descriptor of fieldDescriptors) {
        const fieldName = descriptor.key;
        const responseOnly = isResponseOnlyField(descriptor);
        // Apply the right subSchema variant of the childClass to each variant of this schema
        if (descriptor.childClass) {
            if (!descriptor.childClass.dtoValidators) {
                throw new Error(`Child class ${descriptor.childClass.name} has no dtoValidators`);
            }
            const childBase = descriptor.childClass.dtoValidators.base;
            const childBaseLax = descriptor.childClass.dtoValidators.baseLax;
            const childCreate = descriptor.childClass.dtoValidators.create;
            const childCreateLax = descriptor.childClass.dtoValidators.createLax;
            // if allowPartialOnUpdate is false (the default), we use the create schema for the child class's update schema to enforce all fields being present
            const childUpdate = descriptor.allowPartialOnUpdate
                ? descriptor.childClass.dtoValidators.update
                : descriptor.childClass.dtoValidators.create;

            // apply the childClassTransform function if it is specified
            baseSchema[fieldName] = descriptor.childClassTransform
                ? descriptor.childClassTransform(childBase)
                : childBase;
            baseLaxSchema[fieldName] = descriptor.childClassTransform
                ? descriptor.childClassTransform(childBaseLax)
                : childBaseLax;
            // responseOnly fields are omitted from create and update schemas
            if (!responseOnly) {
                createSchema[fieldName] = optionalIfHasDefault(
                    descriptor.childClassTransform ? descriptor.childClassTransform(childCreate) : childCreate,
                    descriptor.hasDefault,
                );
                createLaxSchema[fieldName] = optionalIfHasDefault(
                    descriptor.childClassTransform ? descriptor.childClassTransform(childCreateLax) : childCreateLax,
                    descriptor.hasDefault,
                );
                const childUpdateSchema = descriptor.childClassTransform
                    ? descriptor.childClassTransform(childUpdate)
                    : childUpdate;
                updateSchema[fieldName] = descriptor.requiredOnUpdate
                    ? childUpdateSchema
                    : // check if the childUpdateSchema is already optional before adding .optional() for allowPartialOnUpdate
                      childUpdateSchema.safeParse(undefined).success
                      ? childUpdateSchema
                      : childUpdateSchema.optional();
            }
        } else if (fieldName === "schemaKey") {
            if (descriptor.schemaKey == undefined) {
                throw new Error(
                    'The schemaKey decorator option is required for all schemas. Please add @validate({ schemaKey: "schemaKey" }) to the class.',
                );
            }
            // automatically generate a schemaKey field for all schemas except update (since it would be redundant)
            const schemaKeySchema = z.literal(descriptor.schemaKey).default(descriptor.schemaKey);
            baseSchema[fieldName] = schemaKeySchema;
            baseLaxSchema[fieldName] = schemaKeySchema;
            createSchema[fieldName] = schemaKeySchema;
            createLaxSchema[fieldName] = schemaKeySchema;
            updateSchema[fieldName] = schemaKeySchema;
        } else {
            if (!descriptor.zodValidator) {
                throw new Error(
                    `No Zod validator specified for field ${fieldName}. Either specify a zodValidator or a childClass.`,
                );
            }
            // Use basic Zod validator if no childClass is specified
            baseSchema[fieldName] = descriptor.zodValidator;
            baseLaxSchema[fieldName] = descriptor.zodValidator;
            // responseOnly fields are omitted from create and update schemas
            if (!responseOnly) {
                createSchema[fieldName] = optionalIfHasDefault(descriptor.zodValidator, descriptor.hasDefault);
                createLaxSchema[fieldName] = optionalIfHasDefault(descriptor.zodValidator, descriptor.hasDefault);
                // every key may be omitted on update, except one flagged as requiredOnUpdate
                updateSchema[fieldName] = descriptor.requiredOnUpdate
                    ? descriptor.zodValidator
                    : descriptor.zodValidator.optional();
            }
        }
    }

    // Return all variants of the schema
    // They should get cast to their respective generated DTO type in the implementing class
    return augmentSchemasWithOpenApiMeta(cls, {
        base: z.strictObject(baseSchema),
        baseLax: z.object(baseLaxSchema),
        create: z.strictObject(createSchema),
        createLax: z.object(createLaxSchema),
        update: z.strictObject(updateSchema),
    });
}
